import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Member } from '../../../core/services/member.service';
import {
  BulkPaymentResult,
  CreateBulkPaymentRequest,
  PaymentService
} from '../../../core/services/payment.service';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';

interface BulkPayRow {
  memberId: string;
  fullName: string;
  expected: number;
  totalPaid: number;
  shortfall: number;
  amount: number;
}

@Component({
  selector: 'app-bulk-pay-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerComponent],
  templateUrl: './bulk-pay-modal.component.html'
})
export class BulkPayModalComponent implements OnChanges {
  private paymentService = inject(PaymentService);

  @Input() open = false;
  @Input() members: Member[] = [];
  @Input() month = new Date().getMonth() + 1;
  @Input() year = new Date().getFullYear();

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<BulkPaymentResult>();

  readonly rows = signal<BulkPayRow[]>([]);
  readonly paymentMethod = signal<string>('Cash');
  readonly reference = signal<string>('');
  readonly errorMessage = signal<string>('');
  readonly isSaving = signal(false);

  readonly months = [
    { value: 1, name: 'January' },   { value: 2, name: 'February' },
    { value: 3, name: 'March' },     { value: 4, name: 'April' },
    { value: 5, name: 'May' },       { value: 6, name: 'June' },
    { value: 7, name: 'July' },      { value: 8, name: 'August' },
    { value: 9, name: 'September' }, { value: 10, name: 'October' },
    { value: 11, name: 'November' }, { value: 12, name: 'December' }
  ];

  years: number[] = [];

  readonly eligibleCount = computed(() => this.rows().filter(r => r.amount > 0).length);
  readonly totalAmount = computed(() =>
    this.rows().reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
  );

  constructor() {
    const cy = new Date().getFullYear();
    for (let i = cy - 5; i <= cy + 1; i++) this.years.push(i);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.errorMessage.set('');
      this.paymentMethod.set('Cash');
      this.reference.set('');
      this.isSaving.set(false);
      this.rebuildRows();
    }
  }

  private rebuildRows(): void {
    const rows: BulkPayRow[] = this.members
      .filter(m => m.id != null)
      .map(m => {
        const expected = Number(m.currentPeriodExpected ?? m.monthlyFee ?? 0);
        const totalPaid = Number(m.currentPeriodTotalPaid ?? 0);
        const shortfall = Math.max(0, expected - totalPaid);
        // Fully-paid rows default to 0 (user can edit up to record an extra payment).
        const amount = shortfall > 0 ? shortfall : 0;
        return {
          memberId: m.id!,
          fullName: m.fullName,
          expected,
          totalPaid,
          shortfall,
          amount
        };
      });
    this.rows.set(rows);
  }

  onMonthYearChange(): void {
    // No backend recheck — caller is expected to have synced the rows.
    // We just present the same selection; users can adjust amounts.
  }

  setAmount(memberId: string, val: number): void {
    const rows = this.rows().map(r =>
      r.memberId === memberId ? { ...r, amount: Number(val) || 0 } : r
    );
    this.rows.set(rows);
  }

  removeRow(memberId: string): void {
    this.rows.set(this.rows().filter(r => r.memberId !== memberId));
  }

  close(): void {
    if (this.isSaving()) return;
    this.closed.emit();
  }

  submit(): void {
    const eligible = this.rows().filter(r => r.amount > 0);
    if (eligible.length === 0) {
      this.errorMessage.set('No eligible members to charge. Make sure amounts are above zero.');
      return;
    }

    const req: CreateBulkPaymentRequest = {
      month: this.month,
      year: this.year,
      paymentMethod: this.paymentMethod(),
      reference: this.reference()?.trim() || null,
      items: eligible.map(r => ({ memberId: r.memberId, amount: r.amount }))
    };

    this.isSaving.set(true);
    this.errorMessage.set('');

    this.paymentService.bulkCreate(req).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res.success && res.data) {
          this.saved.emit(res.data);
        } else {
          this.errorMessage.set(res.message || 'Bulk payment failed.');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.errorMessage.set(err?.error?.message || 'Bulk payment failed.');
      }
    });
  }
}
